<template>
  <div class="tw-h-full tw-flex tw-flex-col tw-bg-white tw-border tw-border-gray-200 tw-rounded-lg tw-min-h-0 tw-overflow-hidden">
    <!-- Compact Session Chat Header -->
    <div class="tw-flex tw-items-center tw-justify-between tw-p-2 tw-border-b tw-border-gray-200 tw-bg-gray-50">
      <div class="tw-flex tw-items-center tw-gap-2">
        <!-- Session Icon -->
        <div class="tw-w-6 tw-h-6 tw-rounded-full tw-flex-shrink-0 tw-bg-primary-100 tw-flex tw-items-center tw-justify-center">
          <icon icon="solar:chat-round-dots-bold-duotone" class="tw-text-primary-600 tw-text-sm" />
        </div>
        
        <!-- Session Info -->
        <div>
          <h3 v-if="!sessionLoading" class="tw-text-sm tw-font-semibold tw-text-gray-900">
            {{ session?.title || 'Session Chat' }}
          </h3>
          <div v-else class="tw-h-4 tw-bg-gray-200 tw-rounded tw-w-24 tw-animate-pulse"></div>
          
          <p v-if="!loading && !sessionLoading" class="tw-text-xs tw-text-gray-600">
            {{ participants.length }} participant{{ participants.length !== 1 ? 's' : '' }} • {{ messages.length }} message{{ messages.length !== 1 ? 's' : '' }}
            <span v-if="session?.speakers?.length > 0" class="tw-text-amber-600 tw-font-medium">
              • {{ session.speakers.length }} speaker{{ session.speakers.length !== 1 ? 's' : '' }}
            </span>
          </p>
          <div v-else class="tw-h-3 tw-bg-gray-200 tw-rounded tw-w-32 tw-animate-pulse"></div>
        </div>
      </div>
      
      <!-- Header Actions -->
      <div class="tw-flex tw-items-center tw-gap-2">
        <button
          @click="refreshMessages"
          :disabled="loading"
          class="tw-p-2 tw-text-gray-500 hover:tw-text-gray-700 hover:tw-bg-gray-100 tw-rounded-lg tw-transition-colors"
          title="Refresh messages"
        >
          <icon icon="solar:refresh-bold-duotone" class="tw-text-lg" />
        </button>
        
        <!-- Participants Toggle -->
        <button
          @click="toggleParticipants"
          class="tw-p-2 tw-text-gray-500 hover:tw-text-gray-700 hover:tw-bg-gray-100 tw-rounded-lg tw-transition-colors"
          title="Show participants"
        >
          <icon icon="solar:users-group-rounded-bold-duotone" class="tw-text-lg" />
        </button>
      </div>
    </div>

    <!-- Main Chat Area -->
    <div class="tw-flex tw-flex-1 tw-min-h-0">
      <!-- Messages Container -->
      <div class="tw-flex-1 tw-flex tw-flex-col tw-min-h-0">
         <!-- Loading State -->
         <div v-if="shouldShowLoading" class="tw-flex-1 tw-flex tw-flex-col tw-items-center tw-justify-center tw-p-8">
           <div class="tw-w-16 tw-h-16 tw-bg-gray-100 tw-rounded-full tw-flex tw-items-center tw-justify-center tw-mb-4">
             <icon icon="line-md:loading-twotone-loop" class="tw-text-2xl tw-text-gray-400" />
           </div>
           <h3 class="tw-text-lg tw-font-medium tw-text-gray-900 tw-mb-2">Loading session chat</h3>
           <p class="tw-text-sm tw-text-gray-600 tw-text-center">Please wait while we load the conversation...</p>
         </div>

         <!-- Messages -->
        <div 
          v-else-if="!error"
          ref="messagesContainer"
          class="tw-flex-1 tw-overflow-y-auto tw-p-2 tw-space-y-2 tw-min-h-0"
          @scroll="handleScroll"
        >
          <!-- Load More Button -->
          <div v-if="showLoadMore" class="tw-flex tw-justify-center tw-py-1">
            <button
              @click="loadMoreMessages"
              :disabled="loadingMore"
              class="tw-flex tw-items-center tw-gap-1 tw-px-2 tw-py-1 tw-bg-gray-100 hover:tw-bg-gray-200 tw-text-gray-700 tw-rounded tw-transition-colors tw-text-xs"
            >
              <icon v-if="loadingMore" icon="line-md:loading-twotone-loop" class="tw-text-xs" />
              <span v-else>Load More</span>
            </button>
          </div>

          <!-- Messages List -->
          <div
            v-for="message in messages"
            :key="message.id"
            :class="[
              'tw-flex tw-items-start tw-gap-2',
              isCurrentUser(message.sender_id) ? 'tw-justify-end' : '',
              isSpeaker(message.sender_id) ? 'speaker-message' : ''
            ]"
          >
            <!-- Sender Avatar (left side for received messages) -->
            <div 
              v-if="!isCurrentUser(message.sender_id)"
              class="tw-w-6 tw-h-6 tw-rounded-full tw-flex-shrink-0 tw-overflow-hidden"
            >
              <img 
                v-if="message.sender?.profile_image" 
                :src="message.sender.profile_image" 
                :alt="message.sender.name"
                class="tw-w-full tw-h-full tw-object-cover"
              />
              <div v-else class="tw-w-full tw-h-full tw-bg-primary-100 tw-flex tw-items-center tw-justify-center">
                <icon icon="solar:user-bold-duotone" class="tw-text-primary-600 tw-text-xs" />
              </div>
            </div>

            <!-- Message Content -->
            <div 
              :class="[
                'tw-flex tw-flex-col tw-max-w-[75%]',
                isCurrentUser(message.sender_id) ? 'tw-items-end' : 'tw-items-start'
              ]"
            >
              <!-- Sender Name (for received messages) -->
              <div 
                v-if="!isCurrentUser(message.sender_id)"
                class="tw-flex tw-items-center tw-gap-1 tw-text-xs tw-font-medium tw-text-gray-600 tw-mb-0.5"
              >
                <span>{{ message.sender?.name || 'Unknown User' }}</span>
                
                <!-- Speaker Badge -->
                <div 
                  v-if="isSpeaker(message.sender_id)"
                  class="tw-flex tw-items-center tw-gap-1 tw-px-0.5 tw-py-0 tw-bg-sky-100 tw-text-sky-800 tw-rounded-full tw-text-xs tw-font-medium tw-border tw-border-sky-200 speaker-badge"
                  title="Session Speaker"
                >
                  <icon icon="solar:microphone-bold-duotone" class="tw-text-sky-600 tw-text-xs" />
                </div>
              </div>

              <!-- Message Bubble -->
              <div 
                :class="[
                  'tw-rounded-lg tw-px-3 tw-py-1.5 tw-text-xs tw-max-w-full tw-break-words tw-border-2 tw-transition-all tw-duration-200',
                  isCurrentUser(message.sender_id) 
                    ? 'tw-bg-primary-600 tw-text-white tw-rounded-tr-none tw-border-primary-600' 
                    : isSpeaker(message.sender_id)
                      ? 'tw-bg-sky-100 tw-text-gray-900 tw-rounded-tl-none tw-border-sky-200'
                      : 'tw-bg-gray-100 tw-text-gray-900 tw-rounded-tl-none tw-border-gray-200'
                ]"
              >
                <!-- Message Content -->
                <div class="tw-text-sm">
                  <!-- Text Message -->
                  <p v-if="message.message_type === 'text' || message.type === 'text'" class="tw-whitespace-pre-wrap">
                    {{ message.content }}
                  </p>
                  
                  <!-- File Message -->
                  <div v-else-if="message.message_type === 'file'" class="tw-flex tw-items-center tw-gap-2">
                    <icon icon="solar:file-bold-duotone" class="tw-text-lg" />
                    <div class="tw-flex-1 tw-min-w-0">
                      <p class="tw-font-medium tw-truncate">{{ getFileName(message.file_url) }}</p>
                      <p class="tw-text-xs tw-opacity-75">{{ getFileSize(message.file_url) }}</p>
                    </div>
                    <a 
                      v-if="message.file_url" 
                      :href="message.file_url" 
                      target="_blank"
                      class="tw-p-1 tw-text-current hover:tw-opacity-75 tw-transition-opacity"
                      title="Download file"
                    >
                      <icon icon="solar:download-bold-duotone" class="tw-text-sm" />
                    </a>
                  </div>

                  <!-- Other Message Types -->
                  <p v-else class="tw-text-xs tw-opacity-75">
                    {{ getMessageTypeText(message.message_type || message.type) }}
                  </p>
                </div>

                <!-- Sending Indicator -->
                <div v-if="message.id && message.id.toString().startsWith('temp-')" class="tw-flex tw-items-center tw-gap-1 tw-mt-1">
                  <icon icon="line-md:loading-twotone-loop" class="tw-text-xs tw-animate-spin tw-opacity-75" />
                  <span class="tw-text-xs tw-opacity-75">Sending...</span>
                </div>
              </div>

              <!-- Message Timestamp -->
              <div class="tw-text-xs tw-text-gray-500 tw-mt-0.5">
                {{ formatTimestamp(message.created_at) }}
              </div>
            </div>

            <!-- Current User Avatar (right side for sent messages) -->
            <div 
              v-if="isCurrentUser(message.sender_id)"
              class="tw-w-6 tw-h-6 tw-rounded-full tw-flex-shrink-0 tw-overflow-hidden"
            >
              <img 
                v-if="currentUser?.profile_image" 
                :src="currentUser.profile_image" 
                :alt="currentUser.name"
                class="tw-w-full tw-h-full tw-object-cover"
              />
              <div v-else class="tw-w-full tw-h-full tw-bg-primary-600 tw-flex tw-items-center tw-justify-center">
                <icon icon="solar:user-bold-duotone" class="tw-text-white tw-text-xs" />
              </div>
            </div>
          </div>

          <!-- Empty State -->
          <div v-if="!loading && !error && messages.length === 0" class="tw-flex-1 tw-flex tw-flex-col tw-items-center tw-justify-center tw-p-8 tw-min-h-[300px]">
            <div class="tw-w-16 tw-h-16 tw-bg-gray-100 tw-rounded-full tw-flex tw-items-center tw-justify-center tw-mb-4">
              <icon icon="solar:chat-round-dots-bold-duotone" class="tw-text-2xl tw-text-gray-400" />
            </div>
            <h3 class="tw-text-lg tw-font-medium tw-text-gray-900 tw-mb-2">No messages yet</h3>
            <p class="tw-text-sm tw-text-gray-600 tw-text-center tw-max-w-sm">
              Start the session conversation by sending a message.
            </p>
            <div class="tw-mt-4 tw-text-xs tw-text-gray-500 tw-text-center">
              <p v-if="participants.length > 1">This is a group chat for all session participants.</p>
              <p v-else-if="participants.length === 1">You're the only participant in this session.</p>
              <p v-else>No participants have joined this session yet.</p>
              <p>Share thoughts, ask questions, or discuss the session content.</p>
            </div>
          </div>

          <!-- Typing Indicator -->
          <div v-if="isTyping && typingUsers.size > 0" class="tw-flex tw-items-center tw-gap-2 tw-text-sm tw-text-gray-500 tw-italic">
            <icon icon="solar:chat-round-dots-bold-duotone" class="tw-text-primary-500 tw-animate-pulse" />
            <span>
              {{ Array.from(typingUsers).length === 1 ? 'Someone is typing...' : 'Several people are typing...' }}
            </span>
          </div>
        </div>

        <!-- Error State -->
        <div v-if="error" class="tw-flex-1 tw-flex tw-flex-col tw-items-center tw-justify-center tw-p-8">
          <div class="tw-w-16 tw-h-16 tw-bg-red-100 tw-rounded-full tw-flex tw-items-center tw-justify-center tw-mb-4">
            <icon icon="solar:warning-bold-duotone" class="tw-text-2xl tw-text-red-500" />
          </div>
          <h3 class="tw-text-lg tw-font-medium tw-text-gray-900 tw-mb-2">Failed to load session chat</h3>
          <p class="tw-text-sm tw-text-gray-600 tw-text-center tw-mb-4">
            {{ error }}
          </p>
          <a-button @click="fetchMessages" intent="primary" size="sm">
            Try Again
          </a-button>
        </div>
      </div>

      <!-- Participants Sidebar -->
      <div 
        v-if="showParticipants"
        class="tw-w-64 tw-border-l tw-border-gray-200 tw-bg-gray-50 tw-flex-shrink-0"
      >
        <SessionParticipantsList 
          :participants="participants"
          :loading="participantsLoading"
          :session="session"
        />
      </div>
    </div>

    <!-- Message Input -->
    <SessionMessageInput 
      v-if="!error && !loading"
      :sessionId="sessionId"
      :disabled="sendingMessage"
      @message-sent="handleMessageSent"
      @message-sending="handleMessageSending"
    />
  </div>
</template>

<script setup>
import { ref, defineProps, onMounted, nextTick, computed, onUnmounted, watch } from 'vue';
import { useAuthStore } from '@/stores';
import messagingApi from '@/api/messages';
import SessionMessageInput from './SessionMessageInput.vue';
import SessionParticipantsList from './SessionParticipantsList.vue';
import realtimeMessagingService from '@/services/realtime-messaging';

const props = defineProps({
  sessionId: {
    type: String,
    required: true
  },
  conversationId: {
    type: String,
    default: null
  },
  session: {
    type: Object,
    default: null
  }
});

// Reactive data
const messages = ref([]);
const loading = ref(true); // Start with loading state
const error = ref(null);
const messagesContainer = ref(null);
const sendingMessage = ref(false);
const isTyping = ref(false);
const typingUsers = ref(new Set());
const participants = ref([]);
const participantsLoading = ref(false);
const session = ref(null);
const sessionLoading = ref(false);
const showParticipants = ref(false);

// Pagination state
const currentPage = ref(1);
const hasMoreMessages = ref(true);
const loadingMore = ref(false);
const showLoadMore = ref(false);

// Get auth store
const authStore = useAuthStore();

// Computed properties
const currentUser = computed(() => authStore.user);

// Determine which conversation ID to use
const effectiveConversationId = computed(() => {
  return props.conversationId || session.value?.conversation_id;
});

// Computed property to determine if we should show loading state
const shouldShowLoading = computed(() => {
  return loading.value;
});

// Methods
const fetchMessages = async () => {
  if (loading.value) return;
  
  loading.value = true;
  error.value = null;
  
  try {
    let conversationId = effectiveConversationId.value;
    
    // If we don't have a conversation ID, fetch it first
    if (!conversationId) {
      const sessionResponse = await messagingApi.conversations.getSession(props.sessionId);
      
      if (sessionResponse.data?.success && sessionResponse.data?.data) {
        conversationId = sessionResponse.data.data.id;
        // Store the conversation ID for future use
        if (session.value) {
          session.value.conversation_id = conversationId;
        }
      } else {
        throw new Error('Failed to get session conversation');
      }
    }
    
    // Now get messages for this conversation
    const messagesResponse = await messagingApi.conversations.getMessages(conversationId, {
      per_page: 50,
      page: currentPage.value
    });
    
    if (messagesResponse.data?.success && messagesResponse.data?.data) {
      const newMessages = messagesResponse.data.data || [];
      
      // Sort messages by date in ascending order (oldest first, newest last)
      const sortedMessages = newMessages.sort((a, b) => {
        const dateA = new Date(a.created_at);
        const dateB = new Date(b.created_at);
        return dateA - dateB;
      });
      
      if (currentPage.value === 1) {
        messages.value = sortedMessages;
      } else {
        messages.value.unshift(...sortedMessages);
      }
      
      hasMoreMessages.value = newMessages.length === 50;
      showLoadMore.value = hasMoreMessages.value;
    }
  } catch (err) {
    console.error('Error fetching session messages:', err);
    error.value = 'Failed to load session messages. Please try again.';
  } finally {
    loading.value = false;
  }
};

const loadMoreMessages = async () => {
  if (loadingMore.value || !hasMoreMessages.value) return;
  
  loadingMore.value = true;
  currentPage.value += 1;
  
  try {
    let conversationId = effectiveConversationId.value;
    
    // If we don't have a conversation ID, fetch it first
    if (!conversationId) {
      const sessionResponse = await messagingApi.conversations.getSession(props.sessionId);
      
      if (sessionResponse.data?.success && sessionResponse.data?.data) {
        conversationId = sessionResponse.data.data.id;
        // Store the conversation ID for future use
        if (session.value) {
          session.value.conversation_id = conversationId;
        }
      } else {
        throw new Error('Failed to get session conversation');
      }
    }
    
    const messagesResponse = await messagingApi.conversations.getMessages(conversationId, {
      per_page: 50,
      page: currentPage.value
    });
    
    if (messagesResponse.data?.success && messagesResponse.data?.data) {
      const newMessages = messagesResponse.data.data || [];
      
      // Sort messages by date in ascending order (oldest first, newest last)
      const sortedMessages = newMessages.sort((a, b) => {
        const dateA = new Date(a.created_at);
        const dateB = new Date(b.created_at);
        return dateA - dateB;
      });
      
      messages.value.unshift(...sortedMessages);
      
      hasMoreMessages.value = newMessages.length === 50;
      showLoadMore.value = hasMoreMessages.value;
    }
  } catch (err) {
    console.error('Error loading more messages:', err);
    currentPage.value -= 1; // Revert page on error
  } finally {
    loadingMore.value = false;
  }
};

const refreshMessages = () => {
  currentPage.value = 1;
  hasMoreMessages.value = true;
  showLoadMore.value = false;
  fetchMessages();
};

const fetchSession = async () => {
  sessionLoading.value = true;
  
  try {
    // If session prop is provided, use it directly
    if (props.session) {
      session.value = {
        id: props.session.id || props.sessionId,
        title: props.session.name || props.session.title || 'Session Chat',
        description: props.session.description || 'Session description',
        start_date: props.session.start_date || '2025-01-01',
        start_time: props.session.start_time || '10:00',
        end_time: props.session.end_date || props.session.end_time || '11:00',
        speakers: props.session.speakers || []
      };
      sessionLoading.value = false;
      return;
    }
    
    // Fallback: Fetch session data from API if no session prop provided
    const sessionResponse = await messagingApi.conversations.getSession(props.sessionId);
    
    if (sessionResponse.data?.success && sessionResponse.data?.data) {
      // Get the session data from the conversation response
      const conversationData = sessionResponse.data.data;
      
      // Fetch the actual session details to get speakers
      try {
        // This would typically come from a sessions API
        // For now, we'll use the conversation data and add mock speakers
        // In a real implementation, you'd fetch from sessions API
        session.value = {
          id: props.sessionId,
          title: conversationData.title || 'Session Chat',
          description: conversationData.description || 'Session description',
          start_date: conversationData.start_date || '2025-01-01',
          start_time: conversationData.start_time || '10:00',
          end_time: conversationData.end_time || '11:00',
          speakers: conversationData.speakers || []
        };
      } catch (sessionErr) {
        console.error('Error fetching session details:', sessionErr);
        // Fallback to basic session data
        session.value = {
          id: props.sessionId,
          title: conversationData.title || 'Session Chat',
          description: conversationData.description || 'Session description',
          start_date: conversationData.start_date || '2025-01-01',
          start_time: conversationData.start_time || '10:00',
          end_time: conversationData.end_time || '11:00',
          speakers: []
        };
      }
    } else {
      throw new Error('Failed to get session conversation');
    }
  } catch (err) {
    console.error('Error fetching session:', err);
    // Fallback to mock data
    session.value = {
      id: props.sessionId,
      title: 'Session Title',
      description: 'Session description',
      start_date: '2025-01-01',
      start_time: '10:00',
      end_time: '11:00',
      speakers: []
    };
  } finally {
    sessionLoading.value = false;
  }
};

const fetchParticipants = async () => {
  participantsLoading.value = true;
  
  try {
    let conversationId = effectiveConversationId.value;
    
    // If we don't have a conversation ID, fetch it first
    if (!conversationId) {
      const sessionResponse = await messagingApi.conversations.getSession(props.sessionId);
      
      if (sessionResponse.data?.success && sessionResponse.data?.data) {
        conversationId = sessionResponse.data.data.id;
        // Store the conversation ID for future use
        if (session.value) {
          session.value.conversation_id = conversationId;
        }
      } else {
        throw new Error('Failed to get session conversation');
      }
    }
    
    const participantsResponse = await messagingApi.conversations.getParticipants(conversationId);
    
    if (participantsResponse.data?.success && participantsResponse.data?.data) {
      // Map participants and add speaker role information
      participants.value = participantsResponse.data.data.map(participant => ({
        ...participant,
        isSpeaker: isSpeaker(participant.id)
      }));
    }
  } catch (err) {
    console.error('Error fetching participants:', err);
    // Fallback to mock data for development with speaker information
    participants.value = [
      { id: '1', name: 'Speaker 1', profile_image: null, isSpeaker: true },
      { id: '2', name: 'Speaker 2', profile_image: null, isSpeaker: true },
      { id: '3', name: 'Attendee 1', profile_image: null, isSpeaker: false }
    ];
  } finally {
    participantsLoading.value = false;
  }
};

const handleMessageSent = async (message) => {
  try {
    console.log('Handling message sent:', message);
    
    // Add the message to UI immediately for better UX
    messages.value.push(message);
    scrollToBottom();
    
    // Get the conversation ID
    let conversationId = effectiveConversationId.value;
    console.log('Effective conversation ID:', conversationId);
    
    if (!conversationId) {
      throw new Error('No conversation ID available - user may not be joined to this session');
    }
    
    console.log('Sending message via API to conversation:', conversationId);
    
    // Send the message via API
    const response = await messagingApi.messages.sendText(conversationId, {
      content: message.content
    });
    
    console.log('API response:', response);
    
    if (response.data?.success && response.data?.data) {
      // Replace the temporary message with the real one from the server
      const realMessage = response.data.data;
      const messageIndex = messages.value.findIndex(m => m.id === message.id);
      
      if (messageIndex !== -1) {
        messages.value[messageIndex] = realMessage;
        console.log('Message replaced with server response');
      } else {
        // If not found, add it to the end (fallback)
        messages.value.push(realMessage);
        console.log('Message added as fallback');
      }
      
      scrollToBottom();
    } else {
      throw new Error('Failed to send message - invalid API response');
    }
  } catch (err) {
    console.error('Error sending message:', err);
    
    // Remove the temporary message on error
    const messageIndex = messages.value.findIndex(m => m.id === message.id);
    if (messageIndex !== -1) {
      messages.value.splice(messageIndex, 1);
    }
    
    // Show error to user (you might want to add a toast notification here)
    error.value = `Failed to send message: ${err.message}`;
  }
};

const handleMessageSending = (sending) => {
  sendingMessage.value = sending;
};

const scrollToBottom = () => {
  nextTick(() => {
    if (messagesContainer.value) {
      messagesContainer.value.scrollTop = messagesContainer.value.scrollHeight;
    }
  });
};

const scrollToBottomAfterLoad = () => {
  if (messages.value.length > 0) {
    scrollToBottom();
  }
};

const handleScroll = () => {
  if (!messagesContainer.value) return;
  
  const { scrollTop } = messagesContainer.value;
  
  // Show load more button when user scrolls near the top
  showLoadMore.value = scrollTop < 100 && hasMoreMessages.value && !loadingMore.value;
};

const toggleParticipants = () => {
  showParticipants.value = !showParticipants.value;
};

// Note: markAsRead function removed as it's not currently used
// Can be re-implemented when marking conversations as read is needed

const isCurrentUser = (senderId) => {
  return currentUser.value?.id === senderId;
};

const isSpeaker = (senderId) => {


  
  if (!session.value?.speakers) return false;
  return session.value.speakers.some(speaker => speaker.id == senderId);
};

const formatTimestamp = (timestamp) => {
  if (!timestamp) return '';
  
  try {
    const date = new Date(timestamp);
    const now = new Date();
    const diffInHours = (now - date) / (1000 * 60 * 60);
    
    if (diffInHours < 24) {
      // Check if the timestamp has a timezone indicator (Z suffix)
      const isUTC = typeof timestamp === 'string' && timestamp.includes('Z');
      
      if (isUTC) {
        // For UTC timestamps, use UTC methods to avoid timezone conversion
        const hours = date.getUTCHours();
        const minutes = date.getUTCMinutes();
        const ampm = hours >= 12 ? 'PM' : 'AM';
        const displayHours = hours % 12 || 12;
        const displayMinutes = minutes.toString().padStart(2, '0');
        return `${displayHours}:${displayMinutes} ${ampm}`;
      } else {
        // For local timestamps, use local methods
        return date.toLocaleTimeString('en-US', { 
          hour: 'numeric', 
          minute: '2-digit',
          hour12: true 
        });
      }
    } else if (diffInHours < 48) {
      return 'Yesterday';
    } else {
      // Check if the timestamp has a timezone indicator (Z suffix)
      const isUTC = typeof timestamp === 'string' && timestamp.includes('Z');
      
      if (isUTC) {
        // For UTC timestamps, use UTC methods to avoid timezone conversion
        const month = date.toLocaleString('en-US', { month: 'short' });
        const day = date.getUTCDate();
        return `${month} ${day}`;
      } else {
        // For local timestamps, use local methods
        return date.toLocaleDateString('en-US', { 
          month: 'short', 
          day: 'numeric' 
        });
      }
    }
  } catch (error) {
    console.error('Error formatting timestamp:', error);
    return '';
  }
};

const getFileName = (url) => {
  if (!url) return 'Unknown File';
  const parts = url.split('/');
  return parts[parts.length - 1];
};

const getFileSize = (url) => {
  if (!url) return 'N/A';
  // This is a placeholder. In a real app, you'd fetch the file size from the server.
  // For now, we'll return a placeholder.
  return '1.2 MB';
};

const getMessageTypeText = (type) => {
  switch (type) {
    case 'text':
      return 'Text Message';
    case 'file':
      return 'File Message';
    case 'image':
      return 'Image Message';
    case 'video':
      return 'Video Message';
    case 'audio':
      return 'Audio Message';
    default:
      return type;
  }
};

// Real-time messaging setup
const setupRealtimeMessaging = async () => {
  try {
    let conversationId = effectiveConversationId.value;
    
    // If we don't have a conversation ID, fetch it first
    if (!conversationId) {
      const sessionResponse = await messagingApi.conversations.getSession(props.sessionId);
      
      if (sessionResponse.data?.success && sessionResponse.data?.data) {
        conversationId = sessionResponse.data.data.id;
        // Store the conversation ID for future use
        if (session.value) {
          session.value.conversation_id = conversationId;
        }
      } else {
        throw new Error('Failed to get session conversation');
      }
    }
    
    // Join the conversation channel
    await realtimeMessagingService.joinConversation(conversationId, {
      showNotifications: false
    });

    // Listen for new messages
    realtimeMessagingService.on('text-message', (event) => {
      messages.value.push(event);
      scrollToBottom();
    });

    // Listen for typing indicators
    realtimeMessagingService.on('typing-started', (event) => {
      const { sender } = event;
      if (sender && !isCurrentUser(sender.id)) {
        typingUsers.value.add(sender.id);
        isTyping.value = true;
      }
    });

    realtimeMessagingService.on('typing-stopped', (event) => {
      const { sender } = event;
      if (sender && !isCurrentUser(sender.id)) {
        typingUsers.value.delete(sender.id);
        isTyping.value = typingUsers.value.size > 0;
      }
    });

    // Listen for other message types
    realtimeMessagingService.on('file-message', (event) => {
      messages.value.push(event);
      scrollToBottom();
    });

    realtimeMessagingService.on('system-message', (event) => {
      messages.value.push(event);
      scrollToBottom();
    });
  } catch (err) {
    console.error('Error setting up real-time messaging:', err);
  }
};

// Cleanup real-time messaging
const cleanupRealtimeMessaging = () => {
  try {
    const conversationId = effectiveConversationId.value || props.sessionId;
    realtimeMessagingService.leaveConversation(conversationId);
    realtimeMessagingService.off('text-message');
    realtimeMessagingService.off('typing-started');
    realtimeMessagingService.off('typing-stopped');
    realtimeMessagingService.off('file-message');
    realtimeMessagingService.off('system-message');
  } catch (err) {
    console.error('Error cleaning up real-time messaging:', err);
  }
};

// Lifecycle
onMounted(async () => {
  try {
    // If session prop is provided, we don't need to fetch it
    if (!props.session) {
      await fetchSession();
    }
    
    await fetchParticipants();
    
    // Set loading to false before fetching messages since fetchMessages has a guard
    loading.value = false;
    await fetchMessages();
    await setupRealtimeMessaging();
    
    // Ensure scroll to bottom after everything is loaded
    nextTick(() => {
      scrollToBottomAfterLoad();
      handleScroll();
    });
  } catch (error) {
    console.error('Error during component initialization:', error);
    loading.value = false;
    error.value = 'Failed to load chat';
  }
});

onUnmounted(() => {
  cleanupRealtimeMessaging();
});

// Watch for session ID changes
watch(() => props.sessionId, async (newSessionId) => {
  if (newSessionId) {
    // Reset state
    messages.value = [];
    participants.value = [];
    currentPage.value = 1;
    hasMoreMessages.value = true;
    showLoadMore.value = false;
    
    // Cleanup previous real-time connection
    cleanupRealtimeMessaging();
    
    // Setup new session
    if (!props.session) {
      await fetchSession();
    }
    await fetchParticipants();
    
    await fetchMessages();
    await setupRealtimeMessaging();
  }
});

// Watch for session prop changes
watch(() => props.session, async (newSession) => {
  if (newSession) {
    // Update session data when prop changes
    session.value = {
      id: newSession.id || props.sessionId,
      title: newSession.name || newSession.title || 'Session Chat',
      description: newSession.description || 'Session description',
      start_date: newSession.start_date || '2025-01-01',
      start_time: newSession.start_time || '10:00',
      end_time: newSession.end_date || newSession.end_time || '11:00',
      speakers: newSession.speakers || []
    };
    
    // Refresh participants to update speaker information
    await fetchParticipants();
  }
}, { immediate: true });
</script>

<style scoped>
/* Custom scrollbar for messages container */
.tw-overflow-y-auto::-webkit-scrollbar {
  width: 4px;
}

.tw-overflow-y-auto::-webkit-scrollbar-track {
  background: transparent;
}

.tw-overflow-y-auto::-webkit-scrollbar-thumb {
  background: #d1d5db;
  border-radius: 2px;
}

.tw-overflow-y-auto::-webkit-scrollbar-thumb:hover {
  background: #9ca3af;
}

/* Ensure proper height constraints */
.tw-h-full {
  height: 100%;
  min-height: 0;
}

/* Smooth scrolling for messages container */
.tw-overflow-y-auto {
  scroll-behavior: smooth;
}

/* Speaker message enhancements */
.speaker-message {
  position: relative;
}


/* Speaker badge animations */
.speaker-badge {
  animation: pulse 2s infinite;
}

@keyframes pulse {
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: 0.8;
  }
}
</style>
